import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { onsiteApiFetch } from '../config/api.js';
import { useAuth } from '../contexts/AuthContext.js';
import type { OnsiteJob, OnsiteClient, OnsiteTechnician } from '../../../api/src/types/onsite-types.js';

interface PrefillPsaTicket {
  id: string;
  ticket_number: string;
  title: string;
  summary?: string;
  customer_name?: string;
  customer_address?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  priority?: string;
}

interface JobForm {
  title: string;
  description: string;
  job_type: string;
  priority: string;
  customer_name: string;
  customer_address: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  client_id: string;
  assigned_tech_id: string;
  assigned_tech_name: string;
  scheduled_start: string;
  scheduled_end: string;
  estimated_duration_minutes: string;
  psa_ticket_number: string;
  notes: string;
}

const EMPTY_FORM: JobForm = {
  title: '',
  description: '',
  job_type: 'other',
  priority: 'normal',
  customer_name: '',
  customer_address: '',
  contact_name: '',
  contact_phone: '',
  contact_email: '',
  client_id: '',
  assigned_tech_id: '',
  assigned_tech_name: '',
  scheduled_start: '',
  scheduled_end: '',
  estimated_duration_minutes: '',
  psa_ticket_number: '',
  notes: '',
};

const JOB_TYPES = [
  { value: 'installation', label: 'Installation' },
  { value: 'repair', label: 'Repair' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'break_fix', label: 'Break/Fix' },
  { value: 'project', label: 'Project' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'other', label: 'Other' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export default function JobCreatePage() {
  const navigate = useNavigate();
  const { orgId } = useAuth();
  const [searchParams] = useSearchParams();
  const psaTicketId = searchParams.get('psa_ticket_id') ?? '';
  const prefilledClientId = searchParams.get('client_id') ?? '';
  const [form, setForm] = useState<JobForm>({ ...EMPTY_FORM, client_id: prefilledClientId });
  const [errors, setErrors] = useState<Partial<Record<keyof JobForm, string>>>({});
  const [prefillError, setPrefillError] = useState<string | null>(null);

  // Prefill from PSA ticket if `?psa_ticket_id=X` is supplied.
  useEffect(() => {
    if (!psaTicketId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await onsiteApiFetch<{ ticket: PrefillPsaTicket }>(
          `/api/v1/onsite/psa/tickets/${encodeURIComponent(psaTicketId)}`,
        );
        if (cancelled || !res.ticket) return;
        const t = res.ticket;
        const priorityMap: Record<string, string> = {
          urgent: 'urgent', critical: 'urgent', high: 'high', medium: 'normal', normal: 'normal', low: 'low',
        };
        setForm(f => ({
          ...f,
          title: t.title ?? '',
          description: t.summary ?? '',
          customer_name: t.customer_name ?? '',
          customer_address: t.customer_address ?? '',
          contact_name: t.contact_name ?? '',
          contact_phone: t.contact_phone ?? '',
          contact_email: t.contact_email ?? '',
          priority: priorityMap[(t.priority ?? 'normal').toLowerCase()] ?? 'normal',
          psa_ticket_number: t.ticket_number ?? '',
        }));
      } catch (e) {
        if (!cancelled) setPrefillError((e as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, [psaTicketId]);

  // Load clients for dropdown
  const { data: clientsData } = useQuery({
    queryKey: ['clients', orgId],
    queryFn: () => onsiteApiFetch<{ clients: OnsiteClient[] }>('/api/v1/onsite/clients'),
    enabled: !!orgId,
  });

  // When clients load and a client_id was in the URL, prefill customer fields
  useEffect(() => {
    if (!prefilledClientId || !clientsData?.clients) return;
    const c = clientsData.clients.find(x => x.id === prefilledClientId);
    if (!c) return;
    setForm(f => ({
      ...f,
      client_id: c.id,
      customer_name: f.customer_name || c.name,
      customer_address: f.customer_address || c.address,
      contact_name: f.contact_name || (c.primary_contact_name ?? ''),
      contact_phone: f.contact_phone || (c.primary_contact_phone ?? ''),
      contact_email: f.contact_email || (c.primary_contact_email ?? ''),
    }));
  }, [prefilledClientId, clientsData?.clients]);

  // Load technicians for dropdown
  const { data: techsData } = useQuery({
    queryKey: ['technicians', orgId],
    queryFn: () => onsiteApiFetch<{ technicians: OnsiteTechnician[] }>('/api/v1/onsite/technicians'),
    enabled: !!orgId,
  });

  const createJob = useMutation({
    mutationFn: (payload: Partial<OnsiteJob>) =>
      onsiteApiFetch<{ job: OnsiteJob }>('/api/v1/onsite/jobs', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: (data) => {
      navigate(`/jobs/${data.job.id}`);
    },
  });

  function validate(): boolean {
    const errs: Partial<Record<keyof JobForm, string>> = {};
    if (!form.title.trim()) errs.title = 'Title is required';
    if (!form.scheduled_start) errs.scheduled_start = 'Start date/time is required';
    if (!form.scheduled_end) errs.scheduled_end = 'End date/time is required';
    if (form.scheduled_start && form.scheduled_end && form.scheduled_start >= form.scheduled_end) {
      errs.scheduled_end = 'End must be after start';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleClientChange(clientId: string) {
    const client = clientsData?.clients?.find(c => c.id === clientId);
    if (client) {
      setForm(f => ({
        ...f,
        client_id: clientId,
        customer_name: client.name,
        customer_address: client.address,
        contact_name: client.primary_contact_name ?? '',
        contact_phone: client.primary_contact_phone ?? '',
        contact_email: client.primary_contact_email ?? '',
      }));
    } else {
      setForm(f => ({ ...f, client_id: '' }));
    }
  }

  function handleTechChange(techId: string) {
    const tech = techsData?.technicians?.find(t => t.id === techId || t.user_id === techId);
    setForm(f => ({
      ...f,
      assigned_tech_id: tech?.user_id ?? techId,
      assigned_tech_name: tech?.name ?? '',
    }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    await createJob.mutateAsync({
      title: form.title,
      description: form.description,
      job_type: form.job_type as OnsiteJob['job_type'],
      priority: form.priority as OnsiteJob['priority'],
      customer_name: form.customer_name,
      customer_address: form.customer_address,
      contact_name: form.contact_name || undefined,
      contact_phone: form.contact_phone || undefined,
      contact_email: form.contact_email || undefined,
      client_id: form.client_id || undefined,
      assigned_tech_id: form.assigned_tech_id || undefined,
      assigned_tech_name: form.assigned_tech_name || undefined,
      scheduled_start: new Date(form.scheduled_start).toISOString(),
      scheduled_end: new Date(form.scheduled_end).toISOString(),
      estimated_duration_minutes: form.estimated_duration_minutes
        ? parseInt(form.estimated_duration_minutes, 10)
        : undefined,
      psa_ticket_number: form.psa_ticket_number || undefined,
      notes: form.notes,
    });
  }

  const clients = clientsData?.clients ?? [];
  const techs = techsData?.technicians ?? [];

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-secondary"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <h1 className="text-xl font-bold text-foreground">New Job</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {createJob.isError && (
            <div className="rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-300">
              {(createJob.error as Error).message}
            </div>
          )}
          {prefillError && (
            <div className="rounded-lg border border-amber-800 bg-amber-950 p-3 text-sm text-amber-300">
              Could not prefill from PSA ticket: {prefillError}
            </div>
          )}

          {/* Job details */}
          <Section title="Job Details">
            <FormField label="Title" required error={errors.title}>
              <input
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Server rack installation"
                className="input"
              />
            </FormField>

            <div className="grid grid-cols-2 gap-4">
              <FormField label="Job Type">
                <select
                  value={form.job_type}
                  onChange={e => setForm(f => ({ ...f, job_type: e.target.value }))}
                  className="input"
                >
                  {JOB_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Priority">
                <select
                  value={form.priority}
                  onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                  className="input"
                >
                  {PRIORITIES.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </FormField>
            </div>

            <FormField label="Description">
              <textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Job description and scope of work..."
                rows={3}
                className="input resize-none"
              />
            </FormField>

            <FormField label="PSA Ticket #">
              <input
                value={form.psa_ticket_number}
                onChange={e => setForm(f => ({ ...f, psa_ticket_number: e.target.value }))}
                placeholder="e.g. TKT-12345"
                className="input"
              />
            </FormField>
          </Section>

          {/* Client */}
          <Section title="Client / Site">
            {clients.length > 0 && (
              <FormField label="Select Client">
                <select
                  value={form.client_id}
                  onChange={e => handleClientChange(e.target.value)}
                  className="input"
                >
                  <option value="">-- Select a client or enter manually --</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </FormField>
            )}
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Company Name">
                <input
                  value={form.customer_name}
                  onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                  placeholder="Client company name"
                  className="input"
                />
              </FormField>
              <FormField label="Site Address">
                <input
                  value={form.customer_address}
                  onChange={e => setForm(f => ({ ...f, customer_address: e.target.value }))}
                  placeholder="123 Main St, City, ST"
                  className="input"
                />
              </FormField>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <FormField label="Contact Name">
                <input
                  value={form.contact_name}
                  onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))}
                  placeholder="On-site contact"
                  className="input"
                />
              </FormField>
              <FormField label="Contact Phone">
                <input
                  type="tel"
                  value={form.contact_phone}
                  onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))}
                  placeholder="(555) 123-4567"
                  className="input"
                />
              </FormField>
              <FormField label="Contact Email">
                <input
                  type="email"
                  value={form.contact_email}
                  onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))}
                  placeholder="contact@client.com"
                  className="input"
                />
              </FormField>
            </div>
          </Section>

          {/* Schedule & Assignment */}
          <Section title="Schedule & Assignment">
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Start" required error={errors.scheduled_start}>
                <input
                  type="datetime-local"
                  value={form.scheduled_start}
                  onChange={e => setForm(f => ({ ...f, scheduled_start: e.target.value }))}
                  className="input"
                />
              </FormField>
              <FormField label="End" required error={errors.scheduled_end}>
                <input
                  type="datetime-local"
                  value={form.scheduled_end}
                  onChange={e => setForm(f => ({ ...f, scheduled_end: e.target.value }))}
                  className="input"
                />
              </FormField>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField label="Estimated Duration (min)">
                <input
                  type="number"
                  value={form.estimated_duration_minutes}
                  onChange={e => setForm(f => ({ ...f, estimated_duration_minutes: e.target.value }))}
                  placeholder="60"
                  min="0"
                  className="input"
                />
              </FormField>
              <FormField label="Assign Technician">
                {techs.length > 0 ? (
                  <select
                    value={form.assigned_tech_id}
                    onChange={e => handleTechChange(e.target.value)}
                    className="input"
                  >
                    <option value="">-- Unassigned --</option>
                    {techs.map(t => (
                      <option key={t.id} value={t.user_id}>{t.name}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    value={form.assigned_tech_name}
                    onChange={e => setForm(f => ({ ...f, assigned_tech_name: e.target.value }))}
                    placeholder="Technician name"
                    className="input"
                  />
                )}
              </FormField>
            </div>
          </Section>

          {/* Notes */}
          <Section title="Notes">
            <FormField label="Job Notes">
              <textarea
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Additional notes, special instructions..."
                rows={3}
                className="input resize-none"
              />
            </FormField>
          </Section>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 rounded-xl border border-border py-3 text-sm text-muted-foreground hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createJob.isPending}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary py-3 font-medium text-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {createJob.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Create Job
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">{title}</h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function FormField({ label, required, error, children }: {
  label: string; required?: boolean; error?: string; children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}
